import { Item, ItemHeader, ItemTitle } from '@ibsys/kit';
import { Share2Icon, StarIcon, UsersIcon } from 'lucide-react';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { cn, getFullName } from '@/shared/lib';
import { Tooltip, TruncatedText } from '@/shared/ui';
import type { TablePreset } from '@/entities/settings';

type Props = {
  preset: TablePreset;
  /** Активный (применённый) пресет — подсвечивается */
  isActive: boolean;
  onApply: () => void;
};

/**
 * Строка пресета в списке: клик применяет пресет. Управление пресетом живёт не здесь, а рядом
 * с кнопкой «Пресеты» ({@link PresetActions}) — строка несёт только название и значки состояния.
 */
export const PresetItem: FC<Props> = ({ preset, isActive, onApply }) => {
  const { t } = useTranslation('tablePresets');
  const sharedLabel =
    preset.owner && !preset.isEditable
      ? t('sharedBy', { owner: getFullName(preset.owner.firstName, preset.owner.lastName) })
      : t('sharedPreset');

  return (
    <Item
      variant="outline"
      size="sm"
      role="button"
      tabIndex={-1}
      onClick={onApply}
      className={cn('cursor-pointer hover:bg-muted/60 p-2 w-full min-w-0', isActive && 'border-primary bg-primary/5')}
    >
      <ItemHeader className="min-w-0 min-h-9 gap-2">
        {preset.isSystem && (
          <Tooltip tooltip={t('systemPreset')}>
            <span aria-label={t('systemPreset')} className="shrink-0 text-muted-foreground">
              <UsersIcon className="size-3.5" />
            </span>
          </Tooltip>
        )}
        {preset.isShared && (
          <Tooltip tooltip={sharedLabel}>
            <span aria-label={sharedLabel} className="shrink-0 text-muted-foreground">
              <Share2Icon className="size-3.5" />
            </span>
          </Tooltip>
        )}
        <ItemTitle className="block w-full min-w-0 text-sm">
          <TruncatedText>{preset.name}</TruncatedText>
        </ItemTitle>
        {preset.pinned && (
          <Tooltip tooltip={t('pinnedToMenu')}>
            <span aria-label={t('pinnedToMenu')} className="shrink-0 text-muted-foreground">
              <StarIcon className="size-3.5 fill-current" />
            </span>
          </Tooltip>
        )}
      </ItemHeader>
    </Item>
  );
};
